import { useMemo } from 'react';
import { Formik, Form } from 'formik';

import { Widget, WidgetBody } from '@@/Widget';

import { TypedPolicy } from '../../../types';
import { useNameValidation } from '../../../validation/useNameValidation';
import { PolicyConfigurationFormSection } from '../../PolicyConfigurationFormSection';
import { PolicyFormFooter } from '../../PolicyFormFooter';

import { DockerRBACFormFields } from './DockerRBACFormFields';
import { DockerRBACFormValues } from './types';
import { useDockerRBACInitialValues } from './useDockerRBACInitialValues';
import { createDockerRBACValidationSchema } from './dockerRBACValidationSchema';

interface Props {
  /** Form submission handler */
  onSubmit: (values: DockerRBACFormValues) => Promise<void>;
  /** Form mode - affects button labels and validation behavior */
  mode: 'create' | 'edit';
  /** Existing policy for edit mode - provides initial values */
  policy?: TypedPolicy;
  /** Wizard back navigation - only used in create mode */
  onBack?: () => void;
  /** Whether back is enabled - only used in create mode */
  canGoBack?: boolean;
}

/**
 * Docker RBAC Policy Form Component
 *
 * Used by:
 * - PolicyCreateView (step 2): mode="create" with wizard navigation props
 * - ConfigTab (edit view): mode="edit" without wizard navigation
 */
export function DockerRBACPolicyForm({
  onSubmit,
  mode,
  policy,
  onBack,
  canGoBack,
}: Props) {
  const initialValues = useDockerRBACInitialValues(policy);
  const originalEnvironmentGroupIds = policy?.EnvironmentGroups || [];
  const nameValidation = useNameValidation(policy?.Id);
  const validationSchema = useMemo(
    () => createDockerRBACValidationSchema(nameValidation),
    [nameValidation]
  );

  return (
    <Formik<DockerRBACFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={mode === 'edit'}
      validateOnMount
    >
      <Form className="form-horizontal">
        <Widget>
          <WidgetBody>
            {mode === 'create' && (
              <PolicyConfigurationFormSection policyType="rbac-docker" />
            )}
            <DockerRBACFormFields isCreating={mode === 'create'} />
            {/* Edit mode: footer inside Widget */}
            {mode === 'edit' && (
              <PolicyFormFooter
                mode={mode}
                policyType="rbac-docker"
                policyId={policy?.Id ?? null}
                originalEnvironmentGroupIds={originalEnvironmentGroupIds}
              />
            )}
          </WidgetBody>
        </Widget>

        {/* Create mode: footer outside Widget with StickyFooter */}
        {mode === 'create' && (
          <PolicyFormFooter
            mode={mode}
            policyType="rbac-docker"
            policyId={policy?.Id ?? null}
            onBack={onBack}
            canGoBack={canGoBack}
          />
        )}
      </Form>
    </Formik>
  );
}
